/**
 * The privacy page, written to be read.
 *
 * THE BRIEF WAS THE PDPA AND THE HOUSE VOICE AT THE SAME TIME, and those two
 * are usually taken to be in tension. They are not. Thailand's Personal Data
 * Protection Act asks a controller to say who it is, what it takes, why it is
 * allowed to, how long it keeps it, and what the person can do about it. Every
 * one of those is a short answer. The wall of clauses on most privacy pages is
 * not the law talking, it is a company hedging, and hedging is exactly the
 * thing this product cannot afford on the one page whose only job is to be
 * believed.
 *
 * So: short headings, short sentences, and a ledger for the part that is
 * actually a ledger. A person should be able to read the whole thing standing
 * up, and a lawyer should be able to point at each PDPA requirement and find
 * it in one line.
 *
 * IT ENUMERATES THE REAL PRODUCT, NOT A TEMPLATE. Every row below corresponds
 * to something this codebase genuinely does: the waitlist form, the account
 * and project records in cloud-api, the app logs cloud-signals keeps, the
 * services that handle data for us, consent-gated PostHog analytics
 * (lib/analytics/consent.ts) and every browser storage key the dashboard
 * writes. If a tracker, a service or a storage key is added, it gets a row
 * here in the same commit, or this page has started lying. Revised
 * 2026-10-25 for the California launch review; the legal wording still needs
 * counsel's review before launch.
 *
 * THE THAI PASS IS A KNOWN FOLLOW UP, and it is why there is not one bare
 * string on this screen. Every sentence is a t() key today, so the Thai
 * translation is a file of values landing in lib/i18n/th.ts and nothing on this
 * page has to be touched. A privacy page a Thai reader cannot read is a
 * compliance problem as well as a courtesy problem, so the note near the top
 * says so out loud rather than leaving them to guess.
 */

import { useEffect } from 'react';
import { Link } from 'react-router';

import { Wordmark } from '../../components/brand/Wordmark';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';

import '../../styles/screens/privacy.css';

/* ------------------------------------------------------------- the facts */

/**
 * Addresses and dates are values, not copy. A translator has nothing to do to
 * either, and both are the kind of thing that has to be exactly right rather
 * than approximately right.
 */
const CONTACT_EMAIL = 'hello@sv-academy.org';
const LAST_UPDATED = '2026-10-25';

/* ------------------------------------------------------------ the screen */

export default function Privacy() {
  useEffect(() => {
    captureEvent('privacy_policy_viewed');
  }, []);

  /**
   * The collection ledger. Four columns because the PDPA asks four questions
   * about every piece of data, and answering three of them and leaving the
   * fourth to a paragraph somewhere else is how retention periods go missing.
   */
  const collected = [
    {
      what: t('Your email address and what you are building, if you join the waitlist'),
      why: t('So we can invite you. Our team sees new sign ups in our internal Telegram chat.'),
      basis: t('You said yes on the form. You can take that back whenever you like.'),
      kept: t('Until you join SV Cloud, or until you ask us to delete it. Whichever comes first.'),
    },
    {
      what: t('Your account: email address, name, how you sign in, your GitHub account name, your plan and credits'),
      why: t('To make your account, sign you in, bill your plan, and email you about your own projects.'),
      basis: t('We need it to give you the thing you asked us for.'),
      kept: t('While your account is open. Delete the account from the Account page and we remove it, except billing records the law makes us keep.'),
    },
    {
      what: t('Your projects: the code we deploy, your database, your files and your keys'),
      why: t('Running your app is the service.'),
      basis: t('We need it to give you the thing you asked us for.'),
      kept: t('While the project exists, and 30 days after you delete it, in case you deleted it by mistake.'),
    },
    {
      what: t('Logs from your app: errors and messages your app writes, and request counts'),
      why: t('So you can see what went wrong, and so we can measure usage for your plan.'),
      basis: t('We need it to give you the thing you asked us for.'),
      kept: t('Error and log messages for 30 days. Request and usage counts for as long as billing needs them.'),
    },
    {
      what: t('How you use this dashboard, only if you allow it'),
      why: t('To find what is confusing and fix it. Page visits and clicks, never a screen recording, never your email address.'),
      basis: t('You pressed Allow. Press Privacy choices at the bottom of any page to change your answer.'),
      kept: t('Up to 12 months, then deleted.'),
    },
  ];

  /** Every service that handles personal data for us, and where. */
  const processors = [
    { name: 'Cloudflare', why: t('Runs the dashboard and your apps, stores files, sends our emails, and checks upload links for bots (Turnstile).') },
    { name: 'Cockroach Labs (AWS, Singapore)', why: t('Stores the databases your apps use.') },
    { name: 'GitHub', why: t('Holds your code. We read the repository you connect.') },
    { name: 'PostHog (United States)', why: t('Dashboard usage analytics, only if you allow it.') },
    { name: 'Telegram', why: t('Tells our team about new waitlist sign ups.') },
  ];

  /** The honest list of what is on a person's own machine. */
  const stored = [
    { name: t('Your sign in'), why: t('Keeps you signed in. Removed when you sign out.') },
    { name: t('Your theme and layout'), why: t('So the page opens the way you set it.') },
    { name: t('Your place in the guided tour'), why: t('So the tour does not start over every time you come back.') },
    { name: t('Your waitlist form'), why: t('So a half typed waitlist form, including your email address, is still there if you come back.') },
    { name: t('Small reminders and a project you started creating'), why: t('So a reminder you dismissed stays dismissed, and a half finished project survives a reload.') },
    { name: t('Your analytics answer'), why: t('So we stop asking.') },
    { name: t('An analytics ID, only if you allow it'), why: t('So PostHog can count visits. Removed when you change your answer to no.') },
  ];

  const never = [
    t('We do not show ads. Not here, not anywhere else.'),
    t('We do not sell your personal information, and we do not share it for advertising.'),
    t('We never record your screen or what you type.'),
    t('We put no advertising pixel and no social widget on this site.'),
  ];

  const rights = [
    t('See what we hold about you.'),
    t('Correct anything that is wrong.'),
    t('Ask us to delete it, or delete your account yourself from the Account page.'),
    t('Take back your consent. It stops there and then, and it does not undo what we did while we had it.'),
  ];

  return (
    <div className="pv">
      <div className="pv__lockup">
        <Link className="pv__home" to="/" aria-label={t('SV Cloud')}>
          <Wordmark />
        </Link>
      </div>

      <header className="pv__head">
        <h1 className="pv__title">{t('Privacy')}</h1>
        <p className="pv__lede">
          {t('What we keep, why we are allowed to keep it, and how to get rid of it.')}
        </p>
        <p className="pv__stamp">
          <span>{t('Last updated')}</span>
          <span className="mono">{LAST_UPDATED}</span>
        </p>
        <p className="pv__note">
          {t('A Thai version of this page is on the way. Until it lands, write to us in Thai and we will answer in Thai.')}
        </p>
      </header>

      <div className="pv__body">
        <section className="pv__section">
          <h2 className="pv__section-title">{t('Who we are')}</h2>
          <p className="pv__say">
            {t('SV Cloud is made by Silicon Valley Innovation Co., Ltd., the company behind SV Academy, in Bangkok, Thailand.')}
          </p>
          <p className="pv__say">
            {t("Under Thailand's Personal Data Protection Act we are the data controller for this site. That means we decide what gets collected here, and we are the ones you hold to it.")}
          </p>
        </section>

        <section className="pv__section">
          <h2 className="pv__section-title">{t('What we keep')}</h2>

          <dl className="pv__ledger">
            {collected.map((row) => (
              <div className="pv__entry" key={row.what}>
                <dt className="pv__entry-title">{row.what}</dt>
                <dd className="pv__entry-body">
                  <p className="pv__why">{row.why}</p>
                  <ul className="pv__meta">
                    <li className="pv__meta-row">
                      <span className="pv__meta-key">{t('Why we may keep it')}</span>
                      <span className="pv__meta-value">{row.basis}</span>
                    </li>
                    <li className="pv__meta-row">
                      <span className="pv__meta-key">{t('How long we keep it')}</span>
                      <span className="pv__meta-value">{row.kept}</span>
                    </li>
                  </ul>
                </dd>
              </div>
            ))}
          </dl>

        </section>

        <section className="pv__section">
          <h2 className="pv__section-title">{t('Who handles it for us')}</h2>
          <dl className="pv__ledger pv__ledger--tight">
            {processors.map((row) => (
              <div className="pv__entry" key={row.name}>
                <dt className="pv__entry-title">{row.name}</dt>
                <dd className="pv__entry-body">
                  <p className="pv__why">{row.why}</p>
                </dd>
              </div>
            ))}
          </dl>
          <p className="pv__say pv__say--quiet">
            {t('Some of these are outside Thailand, so your data may be stored or read in other countries, including the United States and Singapore.')}
          </p>
        </section>

        <section className="pv__section">
          <h2 className="pv__section-title">{t('What we do not do')}</h2>
          <ul className="pv__list">
            {never.map((line) => (
              <li className="pv__item" key={line}>
                {line}
              </li>
            ))}
          </ul>
        </section>

        <section className="pv__section">
          <h2 className="pv__section-title">{t('What your browser keeps')}</h2>
          <p className="pv__say">
            {t("These sit in your own browser's storage on your own machine. We set no cookies.")}
          </p>

          <dl className="pv__ledger pv__ledger--tight">
            {stored.map((row) => (
              <div className="pv__entry" key={row.name}>
                <dt className="pv__entry-title">{row.name}</dt>
                <dd className="pv__entry-body">
                  <p className="pv__why">{row.why}</p>
                </dd>
              </div>
            ))}
          </dl>

          <p className="pv__say">
            {t('Analytics starts only after you press Allow. If your browser sends Global Privacy Control or Do Not Track, we treat that as no and do not ask. Accounts that may belong to students under 18 never get analytics.')}
          </p>
          <p className="pv__say pv__say--quiet">
            {t('You can clear all of it from your browser at any time. The site keeps working. It just forgets your settings.')}
          </p>
        </section>

        <section className="pv__section">
          <h2 className="pv__section-title">{t('Your rights')}</h2>
          <p className="pv__say">{t('Under the PDPA you can ask us to:')}</p>
          <ul className="pv__list">
            {rights.map((line) => (
              <li className="pv__item" key={line}>
                {line}
              </li>
            ))}
          </ul>
          <p className="pv__say">
            {t('If we get it wrong, you can complain to the Office of the Personal Data Protection Committee in Bangkok. You do not have to ask us first.')}
          </p>
        </section>

        <section className="pv__section">
          <h2 className="pv__section-title">{t('If you live in California')}</h2>
          <p className="pv__say">
            {t('You can ask us what personal information we have collected about you, ask us to correct it, and ask us to delete it. We will not treat you differently for asking. We do not sell or share personal information, as California law defines those words.')}
          </p>
          <p className="pv__say">
            {t('Write to the address below. Someone you authorize can ask for you. We may need to confirm the request comes from you before we act on it.')}
          </p>
        </section>

        <section className="pv__section">
          <h2 className="pv__section-title">{t('Children')}</h2>
          <p className="pv__say">
            {t('SV Cloud is not for children under 13, and we do not knowingly collect their information. If you think a child under 13 has given us information, write to us and we will delete it.')}
          </p>
        </section>

        <section className="pv__section">
          <h2 className="pv__section-title">{t('Talk to a person')}</h2>
          <p className="pv__say">
            {t('Write to us and a person answers. It is not a ticket queue.')}
          </p>
          <p className="pv__contact">
            <a className="link-inline mono" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </p>
          <p className="pv__say pv__say--quiet">
            {t('Tell us what you want done and which email address you used. We answer within 30 days, which is what the law gives us, and usually a lot sooner.')}
          </p>
        </section>

        <section className="pv__section">
          <h2 className="pv__section-title">{t('When this page changes')}</h2>
          <p className="pv__say">
            {t('If we change what we collect, we change this page before we change the product, and we put the new date at the top.')}
          </p>
        </section>
      </div>
    </div>
  );
}
